import { AntDesign, MaterialIcons } from '@expo/vector-icons';
import { captureException } from '@sentry/react-native';
import { AxiosError } from 'axios';
import * as Calendar from 'expo-calendar';
import { isAvailableAsync as isCalendarAvailableAsync } from 'expo-calendar/legacy';
import { router, useLocalSearchParams, useNavigation } from 'expo-router';
import { getDistance } from 'geolib';
import { useEffect, useLayoutEffect, useState } from 'react';
import { Linking, Platform, StyleSheet, useWindowDimensions, View } from 'react-native';
import { Divider } from 'react-native-paper';
import Animated, {
  ScrollEvent,
  useAnimatedRef,
  useAnimatedScrollHandler,
  useAnimatedStyle,
  useSharedValue
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useGetVolunteerEventById, useSignUpVolunteerEvent } from '@/api/hooks/volunteerHooks';
import BrandedButton from '@/components/buttons/BrandedButton';
import DetailsHeader from '@/components/DetailsHeader';
import DotsLoader from '@/components/DotsLoader';
import MapComponent from '@/components/MapComponent';
import Text from '@/components/Text';
import PlatformContainer from '@/components/web/PlatformContainer';
import { useAuth } from '@/contexts/authContext';
import { useInfoModal } from '@/contexts/infoModalContext';
import { useCollapsibleHeader } from '@/hooks/useCollapsibleHeader';
import useGetLocation from '@/hooks/useGetLocation';
import { formatDateTimeRange, formatDayTime } from '@/utils/dateFormats';
import { safeJsonParseArray } from '@/utils/firebaseImage';
import { i18n } from '@/utils/i18n';

const VolunteerDetail = () => {
  const { volunteerId } = useLocalSearchParams<{ volunteerId: string }>();
  const { width, height } = useWindowDimensions();
  const { bottom } = useSafeAreaInsets();
  const navigation = useNavigation<any>();

  const { state } = useAuth();
  const { showInfo } = useInfoModal();

  const { data: volunteerDetail, isPending: volunteerPending } = useGetVolunteerEventById({
    id: volunteerId
  });

  const { mutateAsync: signUpVolunteer, isPending } = useSignUpVolunteerEvent();

  const [availableCalendar, setAvailableCalendar] = useState<boolean>(true);

  const maxHeaderHeight = Platform.OS === 'web' ? (width >= 768 ? height * 0.65 : height * 0.4) : height * 0.5;

  // Animation
  const animatedRef = useAnimatedRef<Animated.ScrollView>();
  const scrollOffset = useSharedValue(0);

  const { animatedHeight, progress } = useCollapsibleHeader({
    scrollOffset,
    maxHeaderHeight: maxHeaderHeight,
    minHeaderHeight: 250
  });

  const scrollHandler = useAnimatedScrollHandler((event: ScrollEvent) => {
    if (event.contentOffset.y < maxHeaderHeight) scrollOffset.value = event.contentOffset.y;
  });

  const animatedHeaderStyle = useAnimatedStyle(() => ({
    height: Math.round(animatedHeight.value),
    width: '100%',
    overflow: 'hidden'
  }));

  const animatedDotsStyle = useAnimatedStyle(() => ({
    opacity: progress.value
  }));

  const checkAvailableCalendarAPI = async () => {
    const isCalendarAvailable: boolean = await isCalendarAvailableAsync();
    setAvailableCalendar(isCalendarAvailable);
  };

  useEffect(() => {
    checkAvailableCalendarAPI();
  }, []);

  // Navigation
  useLayoutEffect(() => {
    navigation.setOptions({
      headerBackVisible: false
    });
  }, [navigation]);

  const userLocation = useGetLocation();

  const getOrCreateEventCalendar = async () => {
    const calendars = await Calendar.getCalendars(Calendar.EntityTypes.EVENT);
    const calendar = calendars.find((c) => c.allowsModifications) ?? calendars[0];
    if (calendar) {
      return calendar;
    }
    return Calendar.createCalendar({
      title: 'Broken Shepherds',
      entityType: Calendar.EntityTypes.EVENT
    });
  };

  const showEventCreated = () => {
    showInfo({
      type: 'full',
      title: i18n.t('volunteer_detail.event_created'),
      message: i18n.t('volunteer_detail.event_creation_success', { event_name: volunteerDetail?.name }),
      buttonLabel: i18n.t('mix.close')
    });
  };

  const createCalendarEvent = async () => {
    const calendar = await getOrCreateEventCalendar();
    await calendar.createEvent({
      title: volunteerDetail?.name,
      startDate: volunteerDetail?.startDate,
      endDate: volunteerDetail?.endDate ?? volunteerDetail?.startDate
    });
  };

  const addVolunteerToCalendar = async () => {
    try {
      const permission: Calendar.PermissionResponse = await Calendar.getCalendarPermissions();

      if (permission?.granted) {
        await createCalendarEvent();
        showEventCreated();
      } else if (permission?.canAskAgain) {
        Calendar.requestCalendarPermissions().then(async (value) => {
          if (value.granted) {
            await createCalendarEvent();
            showEventCreated();
          }
        });
      } else {
        Linking.openSettings();
      }
    } catch (e) {
      captureException(e);
      showInfo({
        type: 'full',
        title: i18n.t('volunteer_detail.event_creation_fail'),
        buttonLabel: i18n.t('mix.close')
      });
    }
  };

  const signUpButtonAction = async () => {
    if (state.userId) {
      signUpVolunteer(
        { volunteerId },
        {
          onSuccess: () => {
            showInfo({
              type: 'full',
              title: volunteerDetail?.name ?? '',
              subtitle: volunteerDetail?.description,
              subtitle2: formatDayTime(volunteerDetail?.startDate ?? ''),
              message: i18n.t('volunteer_detail.confirmed'),
              buttonLabel: availableCalendar ? i18n.t('volunteer_detail.add_to_calendar') : i18n.t('mix.close'),
              onButtonPress: () => {
                if (availableCalendar) addVolunteerToCalendar();
              }
            });
          },
          onError: (e) => {
            const axiosError = e as AxiosError;
            const responseData = axiosError.response?.data as { message: string };

            showInfo({
              type: 'full',
              title: i18n.t('volunteer_detail.event_signup_fail'),
              message: responseData?.message ?? '',
              buttonLabel: i18n.t('mix.close')
            });
          }
        }
      );
    } else {
      showInfo({
        type: 'full',
        title: i18n.t('volunteer_detail.you_must_register_title'),
        message: i18n.t('volunteer_detail.you_must_register_message'),
        buttonLabel: i18n.t('volunteer_detail.register_now'),
        onButtonPress: () => {
          if (router.canDismiss()) router.dismissAll();
          router.push('/');
        }
      });
    }
  };

  if (volunteerDetail === undefined || volunteerPending) {
    return (
      <View style={style.container}>
        <DotsLoader />
      </View>
    );
  } else {
    //ToDO: We should define how to calculate distances
    //One option is to the backend to make the calculations
    //If we don't make the calculations in the front we can eliminate 'geolib'
    const distance = getDistance(userLocation ?? { latitude: 0, longitude: 0 }, {
      latitude: volunteerDetail?.location?.coordinates[0] ?? 0,
      longitude: volunteerDetail?.location?.coordinates[1] ?? 0
    });

    const dateTimeRange = formatDateTimeRange(volunteerDetail?.startDate, volunteerDetail?.endDate);

    return (
      <PlatformContainer>
        <View style={style.container}>
          {/* Header */}
          <Animated.View style={animatedHeaderStyle}>
            <DetailsHeader
              thumbnails={safeJsonParseArray(volunteerDetail.thumbnail ?? '[]')}
              animatedOpacityStyle={animatedDotsStyle}
              title={volunteerDetail.name}
              itemId={volunteerId}
              type="volunteer"
            />
          </Animated.View>

          {/* Content */}
          <Animated.ScrollView
            bounces={false}
            overScrollMode={'never'}
            showsVerticalScrollIndicator={false}
            ref={animatedRef}
            onScroll={scrollHandler}
            style={style.scrollView}
            contentContainerStyle={style.content}>
            <View style={style.footer}>
              <Text>{formatDayTime(volunteerDetail.startDate)}</Text>
              <View style={style.distance}>
                <MaterialIcons name="location-pin" size={16} />
                {/* distance in meters / 1609 to convert it to miles */}
                <Text>{`${(distance / 1609).toFixed(1)} miles away`}</Text>
              </View>
            </View>

            <Text style={style.description}>{volunteerDetail.description}</Text>

            <Divider />

            {/* Details */}
            <Text style={style.details}>{i18n.t('mix.details')}</Text>
            <View style={style.detailContainer}>
              <View style={style.detailTypeRow}>
                <AntDesign name="calendar" size={40} color="#808080" />
                {dateTimeRange ? (
                  <View style={style.detailTypeColumn}>
                    <Text fontWeight="700">{dateTimeRange.date}</Text>
                    <Text>{dateTimeRange.time}</Text>
                  </View>
                ) : (
                  <>
                    <Text>{formatDayTime(volunteerDetail.startDate)}</Text>
                  </>
                )}
              </View>

              <View style={style.detailTypeRow}>
                <MaterialIcons name="location-pin" size={40} color={'#808080'} />
                <View style={style.detailTypeColumn}>
                  <Text fontWeight="700">{volunteerDetail.address}</Text>
                  <Text>{`${(distance / 1609).toFixed(1)} miles away`}</Text>
                </View>
              </View>
            </View>
            <View style={style.mapContainer}>
              <MapComponent
                markerCoordinates={[volunteerDetail.location.coordinates[0], volunteerDetail.location.coordinates[1]]}
                markerTitle={volunteerDetail.name}
                initialRegion={{
                  latitude: volunteerDetail.location?.coordinates[0],
                  longitude: volunteerDetail.location?.coordinates[1],
                  latitudeDelta: 0.0922,
                  longitudeDelta: 0.0421
                }}
                markerDescription={volunteerDetail.address}
                showCustomMarker
              />
            </View>
          </Animated.ScrollView>

          {/* SignUp */}
          {(dateTimeRange && new Date(volunteerDetail.endDate!) >= new Date()) ||
            (new Date(volunteerDetail?.startDate ?? '') >= new Date() && state.user?.user.role !== 'NON_PROFIT_ORG' && (
              <View style={[style.signUpView, { paddingBottom: bottom + 5 }]}>
                {dateTimeRange ? (
                  <View style={style.timeRangeRow}>
                    <Text>{dateTimeRange.date}</Text>
                    <Text>{dateTimeRange.time}</Text>
                  </View>
                ) : (
                  <Text style={style.signUpDate}>{formatDayTime(volunteerDetail.startDate)}</Text>
                )}
                <BrandedButton
                  style={style.btnStyle}
                  loading={isPending}
                  onPress={signUpButtonAction}
                  backGroundColor="#00C1EB">
                  {i18n.t('mix.sign_up')}
                </BrandedButton>
              </View>
            ))}
        </View>
      </PlatformContainer>
    );
  }
};

export default VolunteerDetail;

const style = StyleSheet.create({
  flexOne: {
    flex: 1
  },

  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center'
  },

  scrollView: {
    width: '100%'
  },

  content: {
    flexDirection: 'column',
    paddingBottom: 25,
    paddingHorizontal: Platform.OS !== 'web' ? 18 : 0
  },

  details: {
    fontSize: 22,
    color: 'black',
    marginVertical: 25
  },

  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontSize: 12,
    color: 'black',
    opacity: 0.6
  },

  distance: {
    flex: 1,
    justifyContent: 'flex-end',
    flexDirection: 'row',
    gap: 2
  },

  description: {
    marginVertical: 25,
    color: 'black',
    opacity: 0.6
  },

  detailContainer: {
    flexDirection: 'column',
    gap: 10
  },

  detailTypeRow: {
    flexDirection: 'row',
    gap: 15,
    alignItems: 'center'
  },

  detailTypeColumn: {
    flex: 1,
    flexDirection: 'column',
    gap: 5
  },

  btnStyle: {
    marginTop: 20
  },

  mapContainer: {
    width: '100%',
    marginTop: 25
  },

  timeRangeRow: {
    flexDirection: 'row',
    gap: 5
  },

  signUpView: {
    backgroundColor: 'white',
    flexDirection: 'column',
    width: '100%',
    paddingHorizontal: 18,
    paddingTop: 25,
    elevation: 10,
    shadowColor: 'black',
    shadowOffset: {
      width: 0,
      height: 1
    },
    shadowOpacity: 0.12
  },

  signUpDate: {
    color: 'black',
    opacity: 0.6,
    fontSize: 14
  }
});
